import React, { useEffect, useRef, useContext } from "react";
import { Link, useLocation } from "react-router-dom";
import { Icon } from "@iconify/react";
import { store } from "../../../../Store";

import { ExploreIcon } from "../../../components/explore-icon";

import { useAccessibility } from "../../../../../hooks/useAccessibility";
import { usePublicHeaderDropdown } from "../../../../../hooks/header/usePublicHeaderDropdown";
import { DropdownNotificationButton } from "../../../components/notification-button";
import { MenuLanguageSelector } from "../../../../language/languageSelector";
import { FocusScope } from "react-aria";
import { PlansDropdownPublic } from "./PlansDropdownPublic";
import { HeaderQuickActions } from "./HeaderQuickActions";

/**
 * Componente que devuelve el dropdown con las opciones en modo responsive. 
 * NO LOGUEADO (blog, comunidad, notificaciones, explorar, ajustes y contacto)
 * @param {object} textosInterfaz - contiene los textos de interfaz
 * @returns {JSX.Element} DropdownResponsive
 */
export const DropdownResponsive = ({ textosInterfaz }) => {
    const location = useLocation();

    const globalState = useContext(store);
    const accessibility = useAccessibility();
    const accessibilityButtonRef = useRef(null);

    const showADropdown = usePublicHeaderDropdown();
    const visibleDropdown = showADropdown?.state?.visible;

    const triggerButton = globalState?.state?.publicHeaderDropdown?.triggerButtonRef;
    const ref = useRef(null);

    const { state } = globalState;
    const country = state?.pais?.toLowerCase();

    /**Función que permite cerrar el dropdown dando click fuera de él
    * @param {event} event 
    */
    const handleOutsideClick = (event) => {
        if (triggerButton?.current?.contains(event.target)) return;
        if (ref.current && !ref.current.contains(event.target)) {
            showADropdown.setVisible(false);
        }
    };

    useEffect(() => {
        document.addEventListener('click', handleOutsideClick);

        return () => {
            document.removeEventListener('click', handleOutsideClick);
        };
    }, [triggerButton]);

    /*cierra el dropdown al hacer scroll al inicio de la página */
    useEffect(() => {
        window.addEventListener("scroll", () => {
            if (window.scrollY >= 1 && window.scrollY < 10) {
                showADropdown.setVisible(false);
            }
        });
    }, [visibleDropdown]);

    /*Asigna el botón que abre las opciones de accesibilidad */
    useEffect(() => {
        if (accessibilityButtonRef.current) {
            globalState.dispatch({
                type: "CHANGE",
                path: "accessibility.triggerButton",
                value: accessibilityButtonRef,
            });
        }
    }, [accessibilityButtonRef]);

    const handleKeyDown = (e) => {
        if (e.key === "Escape") {
            showADropdown.setVisible(false);
        }
    };

    return (
        <div
            className={`dropdownMenuResponsive ${visibleDropdown ? 'activeMode' : 'inactiveMode'} `}
            role="menu"
            aria-label={textosInterfaz?.menuResponsive_label || "Menú principal"}
            onKeyDown={handleKeyDown}
        >
            <FocusScope contain restoreFocus autoFocus >
                <ul ref={ref} aria-label={textosInterfaz?.menuResponsive_list || "Opciones del menú"}>
                    {location.pathname !== '/institucional/formulario' ?
                        <>
                            <li className={`${visibleDropdown ? 'activeMode' : 'inactiveMode'}`}>
                                <Link
                                    to="/libros"
                                    role="menuitem"
                                    className="link-principal d-flex justify-content-center align-items-center focusable-red"
                                    onClick={() => showADropdown.setVisible(false)}
                                    style={{fontWeight: 700}}
                                >
                                    {textosInterfaz?.coleccion || 'Conoce la colección'}
                                </Link>
                            </li>
                        </>
                        :

                        <li className={`${visibleDropdown ? 'activeMode' : 'inactiveMode'}`}>
                            <Link
                                to={"/"}
                                role="menuitem"
                                className="link-principal d-flex justify-content-center align-items-center focusable-red"
                                onClick={() => showADropdown.setVisible(false)}
                            >
                                <ExploreIcon colorLetra={'var(--onprimary-color)'} className="mx-2" />

                                {textosInterfaz?.explorar || 'Explorar'}
                            </Link>
                        </li>
                    }
                    <div role="separator" className={`divider ${visibleDropdown ? 'activeMode' : 'inactiveMode'}`} ></div>

                    
                    <PlansDropdownPublic 
                        textosInterfaz={textosInterfaz}
                        showADropdown={showADropdown}
                        visibleDropdown={visibleDropdown}
                    />
                        
                    <div role="separator" className={`divider ${visibleDropdown ? 'activeMode' : 'inactiveMode'}`} ></div>

                    <li className={`${visibleDropdown ? 'activeMode' : 'inactiveMode'}`}>
                        <Link
                            to="/club-makemake"
                            role="menuitem"
                            style={{fontWeight: 700}}
                            className="link-principal d-flex justify-content-center align-items-center focusable-red"
                            onClick={() => showADropdown.setVisible(false)}
                        >
                            {textosInterfaz?.recursos2 || 'Recursos'}
                        </Link>
                    </li>
                    <div role="separator" className={`divider ${visibleDropdown ? 'activeMode' : 'inactiveMode'}`} ></div>
                    <li className={`${visibleDropdown ? 'activeMode' : 'inactiveMode'}`}>
                        <HeaderQuickActions 
                            textosInterfaz={textosInterfaz}
                            cerrarVentana={() => showADropdown.setVisible(false)}
                        />
                    </li>
                    <li className={`${visibleDropdown ? 'activeMode' : 'inactiveMode'} d-flex justify-content-center`}>
                        <Link
                            role="menuitem"
                            to="/login"
                            className={`focusable-red buttonv2 button-primary`}
                            onClick={() => showADropdown.setVisible(false)}
                        >
                            <Icon icon="wordpress:login" className="onprimary-inverse--color" width={30} />
                            <span className="ml-1">{textosInterfaz?.ingresar || 'Ingresar'}</span>
                        </Link>
                    </li>
                    <li className={`close-options ${visibleDropdown ? 'activeMode' : 'inactiveMode'}`}>
                        <button role="menuitem" onClick={() => showADropdown.setVisible(false)} aria-label={textosInterfaz?.button_close_list_alt || "Cerrar la lista desplegable"} className="w-100 focusable-red ">
                            {textosInterfaz?.button_close_list || "Cerrar"}
                        </button>
                    </li>
                </ul>
            </FocusScope>
        </div>

    )
}